/**
 * openSkyConnector.ts
 *
 * Fetches live aircraft positions from OpenSky Network via Lambda proxy
 * and converts state vectors to a stride-5 Float32Array:
 *   [lon, lat, altM, normalizedSpeed, rotationRad]
 *
 * CORS: Routes through Lambda proxy at /api/geo/opensky (OAuth2 bearer token
 *       caching handled server-side — never exposed to client).
 * Refresh interval: 10 seconds (OpenSky updates state vectors ~every 10s).
 *
 * Antimeridian handling: If bbox.lonMin > bbox.lonMax (Pacific view), issues
 * two fetches and merges the results.
 */

/** Number of floats per aircraft marker: lon, lat, altM, normSpeed, rotationRad */
export const AIRCRAFT_STRIDE = 5;

/** Bbox for OpenSky fetch */
export interface AircraftBbox {
  lonMin: number;
  latMin: number;
  lonMax: number;
  latMax: number;
}

/**
 * Per-aircraft snapshot (parsed from one OpenSky state vector row).
 * Used by AircraftInterpolator for dead-reckoning between polls.
 */
export interface AircraftSnapshot {
  icao24: string;
  callsign: string;
  lon: number;
  lat: number;
  altM: number;
  velocityMs: number;
  headingDeg: number;
  timestampMs: number;
}

/**
 * Parse raw OpenSky state vector arrays into AircraftSnapshot objects.
 *
 * State vector field indices (from OpenSky REST API docs):
 *   [0]  icao24        string
 *   [1]  callsign      string | null
 *   [3]  time_position int | null (Unix seconds of last position)
 *   [5]  longitude     float | null
 *   [6]  latitude      float | null
 *   [7]  baro_altitude float | null (meters MSL)
 *   [8]  on_ground     boolean
 *   [9]  velocity      float | null (m/s ground speed)
 *   [10] true_track    float | null (degrees, 0=north, clockwise)
 *   [13] geo_altitude  float | null (meters above WGS84 ellipsoid)
 *
 * Filters out: on_ground=true, null longitude, null latitude.
 */
export function parseOpenSkyStates(states: unknown[][]): AircraftSnapshot[] {
  return states
    .filter((s) =>
      s[5] != null &&    // longitude not null
      s[6] != null &&    // latitude not null
      s[8] === false     // not on_ground
    )
    .map((s) => ({
      icao24:      (s[0] as string) ?? '',
      callsign:    ((s[1] as string | null)?.trim()) ?? '',
      lon:         s[5] as number,
      lat:         s[6] as number,
      altM:        (s[13] as number | null) ?? (s[7] as number | null) ?? 10000,
      velocityMs:  (s[9]  as number | null) ?? 0,
      headingDeg:  (s[10] as number | null) ?? 0,
      timestampMs: ((s[3] as number | null) ?? Math.floor(Date.now() / 1000)) * 1000,
    }));
}

/**
 * Fetch OpenSky aircraft within a bounding box via Lambda proxy.
 *
 * Returns stride-5 Float32Array: [lon, lat, altM, normalizedSpeed, rotationRad] per aircraft.
 * normalizedSpeed = min(velocityMs / 250, 1.0)
 * rotationRad = headingDeg * PI / 180
 *
 * Returns null if:
 * - json.states is absent or not an array
 * - resp.status === 503 (Lambda credentials not configured) — also logs a warning
 *
 * Throws on other HTTP errors (caller should handle).
 *
 * Antimeridian: if bbox.lonMin > bbox.lonMax, issues two fetches (split at ±180)
 * and merges results.
 */
export async function fetchOpenSkyAircraft(bbox: AircraftBbox): Promise<Float32Array | null> {
  const baseUrl = (process.env.REACT_APP_API_URL ?? '') + '/api/geo/opensky';

  // Detect antimeridian crossing
  if (bbox.lonMin > bbox.lonMax) {
    // Two fetches: [lonMin, 180] and [-180, lonMax]
    const [data1, data2] = await Promise.all([
      fetchOpenSkySingle(baseUrl, bbox.latMin, bbox.lonMin, bbox.latMax, 180),
      fetchOpenSkySingle(baseUrl, bbox.latMin, -180, bbox.latMax, bbox.lonMax),
    ]);
    if (data1 === null && data2 === null) return null;
    const states1 = data1 ?? [];
    const states2 = data2 ?? [];
    const merged = [...states1, ...states2];
    return buildFloat32Array(merged);
  }

  // Normal single fetch
  const states = await fetchOpenSkySingle(baseUrl, bbox.latMin, bbox.lonMin, bbox.latMax, bbox.lonMax);
  if (states === null) return null;
  return buildFloat32Array(states);
}

/**
 * Internal: fetch one OpenSky bbox. Returns parsed snapshots or null on 503/no states.
 */
async function fetchOpenSkySingle(
  baseUrl: string,
  latMin: number,
  lonMin: number,
  latMax: number,
  lonMax: number
): Promise<AircraftSnapshot[] | null> {
  // Build URL with query params — use string concatenation for relative URL compat in test env
  const params = new URLSearchParams({
    lamin: String(latMin),
    lomin: String(lonMin),
    lamax: String(latMax),
    lomax: String(lonMax),
  });
  const fullUrl = `${baseUrl}?${params.toString()}`;

  const resp = await fetch(fullUrl);

  if (resp.status === 503) {
    console.warn('[OpenSky] credentials not configured — aircraft layer unavailable');
    return null;
  }

  if (!resp.ok) {
    throw resp;
  }

  const json = await resp.json();
  if (!json.states || !Array.isArray(json.states)) {
    return null;
  }

  return parseOpenSkyStates(json.states as unknown[][]);
}

/**
 * Build stride-5 Float32Array from AircraftSnapshot array.
 */
function buildFloat32Array(snapshots: AircraftSnapshot[]): Float32Array {
  const buf = new Float32Array(snapshots.length * AIRCRAFT_STRIDE);
  snapshots.forEach((snap, i) => {
    const rotRad = (snap.headingDeg * Math.PI) / 180;
    const normSpeed = Math.min(snap.velocityMs / 250, 1.0);
    buf[i * AIRCRAFT_STRIDE + 0] = snap.lon;
    buf[i * AIRCRAFT_STRIDE + 1] = snap.lat;
    buf[i * AIRCRAFT_STRIDE + 2] = snap.altM;
    buf[i * AIRCRAFT_STRIDE + 3] = normSpeed;
    buf[i * AIRCRAFT_STRIDE + 4] = rotRad;
  });
  return buf;
}
